import * as XLSX from "xlsx";

export interface ImportColumn {
  header: string;
  field: string;
  required?: boolean;
  type: "text" | "int" | "date";
}

export const IMPORT_COLUMNS: ImportColumn[] = [
  { header: "姓名", field: "name", type: "text" },
  { header: "性别", field: "gender", type: "text" },
  { header: "年龄", field: "age", type: "int" },
  { header: "出生年月日", field: "birth_date", type: "date" },
  { header: "身高", field: "height", type: "int" },
  { header: "体重", field: "weight", type: "int" },
  { header: "学历", field: "education", type: "text" },
  { header: "婚姻状况", field: "marital_status", type: "text" },
  { header: "子女情况", field: "children_status", type: "text" },
  { header: "现居城市", field: "residence", type: "text" },
  { header: "籍贯", field: "hometown", type: "text" },
  { header: "职业", field: "occupation", type: "text" },
  { header: "年收入", field: "income", type: "text" },
  { header: "住房情况", field: "house_status", type: "text" },
  { header: "车辆情况", field: "car_status", type: "text" },
  { header: "手机号", field: "phone", type: "text" },
  { header: "微信号", field: "wechat", type: "text" },
  { header: "兴趣爱好", field: "hobbies", type: "text" },
  { header: "自我介绍", field: "self_intro", type: "text" },
  { header: "匹配记录", field: "matchmaker_note", type: "text" },
  { header: "期望年龄起", field: "partner_age_min", type: "int" },
  { header: "期望年龄止", field: "partner_age_max", type: "int" },
  { header: "择偶要求", field: "partner_requirements", type: "text" },
];

export const HEADER_ALIASES: Record<string, string> = {
  姓名: "name",
  名字: "name",
  会员姓名: "name",
  性别: "gender",
  年龄: "age",
  出生年月日: "birth_date",
  出生日期: "birth_date",
  出生年月: "birth_date",
  出生: "birth_date",
  生日: "birth_date",
  身高: "height",
  体重: "weight",
  学历: "education",
  婚姻状况: "marital_status",
  婚姻: "marital_status",
  婚史: "marital_status",
  子女情况: "children_status",
  子女: "children_status",
  家庭情况: "children_status",
  现居城市: "residence",
  现居地: "residence",
  城市: "residence",
  户口所在地: "residence",
  户口: "residence",
  所在地: "residence",
  工作地区: "residence",
  工作地点: "residence",
  籍贯: "hometown",
  家乡: "hometown",
  职业: "occupation",
  工作: "occupation",
  月收入: "income",
  收入: "income",
  年收入: "income",
  年收入状况: "income",
  收入状况: "income",
  住房情况: "house_status",
  住房: "house_status",
  房产: "house_status",
  车辆情况: "car_status",
  车辆: "car_status",
  车况: "car_status",
  购车情况: "car_status",
  购车: "car_status",
  手机号: "phone",
  手机号码: "phone",
  联系电话: "phone",
  电话: "phone",
  微信号: "wechat",
  微信: "wechat",
  兴趣爱好: "hobbies",
  爱好: "hobbies",
  自我介绍: "self_intro",
  介绍: "self_intro",
  匹配记录: "matchmaker_note",
  匹配情况: "matchmaker_note",
  红娘印象: "matchmaker_note",
  印象: "matchmaker_note",
  期望年龄起: "partner_age_min",
  期望对方年龄起: "partner_age_min",
  年龄起: "partner_age_min",
  期望年龄止: "partner_age_max",
  期望对方年龄止: "partner_age_max",
  年龄止: "partner_age_max",
  择偶要求: "partner_requirements",
  择偶条件: "partner_requirements",
  期望对象: "partner_requirements",
  期望对象要求: "partner_requirements",
  期望的另一半: "partner_requirements",
};

export interface ParsedRow {
  data: Record<string, string | number | null>;
  errors: string[];
}

export interface ParseResult {
  rows: ParsedRow[];
  validCount: number;
  errorCount: number;
  skippedHeaders: string[];
  mode?: "table" | "card";
}

function normalizeHeader(header: string): string {
  return String(header).replace(/[^\u4e00-\u9fa5]/g, "");
}

const ALIAS_LOOKUP: Record<string, string> = Object.entries(HEADER_ALIASES).reduce(
  (acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  },
  {} as Record<string, string>,
);

const SECTION_WORDS = new Set([
  "基本情况",
  "基本信息",
  "个人情况",
  "个人资料",
  "会员资料",
  "资料",
  "个人信息",
  "概况",
  "简介",
  "择偶要求",
  "会员信息",
  "会员编号",
  "编号",
  "会员卡号",
]);

export function normalizeGender(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (["男", "先生", "男士", "male", "m"].includes(lower) || lower.includes("男")) return "男";
  if (["女", "女士", "小姐", "female", "f"].includes(lower) || lower.includes("女")) return "女";
  return "";
}

function buildIsoDate(year: string, month: string, day: string): string | null {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isFinite(y) || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function toDate(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str) return null;

  const separated = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(str);
  if (separated) return buildIsoDate(separated[1], separated[2], separated[3]);

  const chinese = /^(\d{4})年(\d{1,2})月(\d{1,2})日?$/.exec(str);
  if (chinese) return buildIsoDate(chinese[1], chinese[2], chinese[3]);

  // 只写了年份，例如「1967年」「1967」→ 一律按 1 月 1 日处理
  const yearOnly = /^(\d{4})年?$/.exec(str);
  if (yearOnly) return `${yearOnly[1]}-01-01`;

  const yearMonth = /^(\d{4})年(\d{1,2})月$/.exec(str);
  if (yearMonth) return buildIsoDate(yearMonth[1], yearMonth[2], "1");

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    return buildIsoDate(
      String(parsed.getFullYear()),
      String(parsed.getMonth() + 1),
      String(parsed.getDate()),
    );
  }
  return null;
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = parseInt(String(value).replace(/[^\d]/g, ""), 10);
  return Number.isNaN(num) ? null : num;
}

function finalizeMapped(
  mapped: Record<string, string | number | null>,
): Record<string, string | number | null> {
  IMPORT_COLUMNS.forEach((col) => {
    const current = mapped[col.field];
    if (col.type === "int") {
      mapped[col.field] = toInt(current);
    } else if (col.type === "date") {
      mapped[col.field] = toDate(current);
    } else {
      const str = current === null || current === undefined ? "" : String(current).trim();
      mapped[col.field] = str ? str : null;
    }
  });
  return mapped;
}

/** 表头 + 数据行的标准表格 */
export function buildRows(raw: Record<string, unknown>[]): ParseResult {
  const rows: ParsedRow[] = [];
  const skipped = new Set<string>();
  let validCount = 0;
  let errorCount = 0;

  raw.forEach((record) => {
    const mapped: Record<string, string | number | null> = {};
    let hasValue = false;

    Object.entries(record).forEach(([key, value]) => {
      const field = ALIAS_LOOKUP[normalizeHeader(key)];
      if (!field) {
        if (normalizeHeader(key)) skipped.add(key);
        return;
      }
      const str = String(value ?? "").trim();
      if (str) hasValue = true;
      mapped[field] = str;
    });

    if (!hasValue) return;

    if (mapped.gender !== undefined) {
      mapped.gender = normalizeGender(String(mapped.gender ?? "")) || null;
    }

    const finalized = finalizeMapped(mapped);
    rows.push({ data: finalized, errors: [] });
    validCount += 1;
  });

  void errorCount;
  return { rows, validCount, errorCount, skippedHeaders: Array.from(skipped), mode: "table" };
}

/** 「字段名：值」一行一项的卡片式文件 */
export function buildRowsFreeform(grid: string[][]): ParseResult {
  const records: Record<string, string>[] = [];
  const skipped = new Set<string>();
  let current: Record<string, string> = {};
  let hasContent = false;

  const flush = () => {
    if (Object.keys(current).length > 0) records.push(current);
  };

  grid.forEach((row) => {
    row.forEach((cellRaw) => {
      const cell = String(cellRaw ?? "").trim();
      if (!cell) return;

      const match = /^([^:：]{1,24})[:：]\s*(.*)$/.exec(cell);
      if (match) {
        const label = match[1].trim();
        const value = match[2].trim();
        const field = ALIAS_LOOKUP[normalizeHeader(label)];
        if (!field) {
          skipped.add(label);
          return;
        }
        if (!value) return;
        current[field] = current[field] ? `${current[field]}；${value}` : value;
        hasContent = true;
        return;
      }

      const norm = normalizeHeader(cell);
      if (SECTION_WORDS.has(norm)) return;

      const isNameCandidate = !/\d/.test(cell) && !/[：:]/.test(cell) && cell.length <= 12;
      if (isNameCandidate) {
        if (hasContent) {
          flush();
          current = {};
          hasContent = false;
        }
        current.name = cell;
        hasContent = true;
      }
    });
  });

  flush();

  const rows: ParsedRow[] = records.map((record) => {
    const mapped: Record<string, string | number | null> = { ...record };
    if (mapped.gender !== undefined) {
      mapped.gender = normalizeGender(String(mapped.gender ?? "")) || null;
    }
    return { data: finalizeMapped(mapped), errors: [] };
  });

  return {
    rows,
    validCount: rows.length,
    errorCount: 0,
    skippedHeaders: Array.from(skipped),
    mode: "card",
  };
}

/** 把整段粘贴的文本按「一行一项」拆开，复用卡片式识别逻辑 */
export function parseTextContent(text: string): ParseResult {
  const grid = text
    .split(/\r?\n/)
    .map((line) =>
      line
        .split(/\t/)
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0),
    )
    .filter((cells) => cells.length > 0);
  return buildRowsFreeform(grid);
}

function sheetToGrid(sheet: XLSX.WorkSheet): string[][] {
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
  });
  return rows.map((row) =>
    Array.isArray(row) ? row.map((cell) => String(cell ?? "").trim()) : [],
  );
}

function isTableMode(grid: string[][]): boolean {
  return grid.some((row) => {
    const hits = row.filter((cell) => cell && ALIAS_LOOKUP[normalizeHeader(cell)]).length;
    return hits >= 2;
  });
}

export function parseWorkbookFile(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const buffer = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(buffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          reject(new Error("文件中没有可读取的工作表"));
          return;
        }
        const sheet = workbook.Sheets[firstSheetName];
        const grid = sheetToGrid(sheet);
        if (grid.length === 0) {
          reject(new Error("表格内容为空，请检查是否填写了数据"));
          return;
        }

        if (isTableMode(grid)) {
          const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
            defval: "",
            raw: false,
          });
          resolve(buildRows(raw));
        } else {
          resolve(buildRowsFreeform(grid));
        }
      } catch {
        reject(new Error("文件解析失败，请确认是有效的 Excel 或 CSV 文件"));
      }
    };
    reader.onerror = () => reject(new Error("文件读取失败，请重试"));
    reader.readAsArrayBuffer(file);
  });
}

export const TEMPLATE_HEADERS = IMPORT_COLUMNS.map((col) =>
  col.required ? `${col.header}*` : col.header,
);

export function downloadTemplate(): void {
  const headSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  headSheet["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 16 }));

  const guideRows: (string | number)[][] = [
    ["字段", "是否必填", "说明 / 可选值"],
    ...IMPORT_COLUMNS.map((col) => [
      col.header,
      col.required ? "必填" : "选填",
      col.type === "int"
        ? "请输入数字"
        : col.type === "date"
          ? "如 1965-03-02 或 1965/3/2"
          : col.field === "gender"
            ? "男"
            : "文本内容",
    ]),
  ];
  const guideSheet = XLSX.utils.aoa_to_sheet(guideRows);
  guideSheet["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 30 }];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, headSheet, "会员资料");
  XLSX.utils.book_append_sheet(book, guideSheet, "填写说明");
  XLSX.writeFile(book, "锦伴金秋-会员导入模板.xlsx");
}